/**
 * The rides list and calendar filters (docs/RIDE_SOURCES_UI_PLAN.md §4.4):
 * source, indoor / outdoor and power, remembered on this device. They
 * narrow what is listed only; totals, fitness and the coach stay whole.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { axiosClient } from '../lib/api.ts';
import { PROVIDERS, isProvider, type Provider } from '../lib/providers.ts';
import type { RideScope } from './useRides.ts';
import { readStored, writeStored } from './useStoredChoice.ts';

export const RIDE_FILTERS_KEY = 'rides_filters';

export type WhereFilter = 'all' | 'indoor' | 'outdoor';
export type PowerFilter = 'all' | 'with' | 'without';

export interface RideFilters {
  providers: Provider[];
  where: WhereFilter;
  power: PowerFilter;
}

export const NO_FILTERS: RideFilters = { providers: [], where: 'all', power: 'all' };

const WHERE: readonly WhereFilter[] = ['all', 'indoor', 'outdoor'];
const POWER: readonly PowerFilter[] = ['all', 'with', 'without'];

/** Stored filters, with anything unknown dropped. */
export function parseFilters(raw: string | null): RideFilters {
  try {
    const value = JSON.parse(raw ?? '') as Partial<RideFilters>;
    return {
      providers: Array.isArray(value.providers) ? value.providers.filter(isProvider) : [],
      where: WHERE.find((w) => w === value.where) ?? 'all',
      power: POWER.find((p) => p === value.power) ?? 'all',
    };
  } catch {
    return NO_FILTERS;
  }
}

export function isFiltered(filters: RideFilters): boolean {
  return filters.providers.length > 0 || filters.where !== 'all' || filters.power !== 'all';
}

/** ``GET /rides`` params; power only applies to rides (scope ``rides``). */
export function filterParams(filters: RideFilters, scope: RideScope): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.providers.length) params.provider = filters.providers.join(',');
  if (filters.where !== 'all') params.indoor = String(filters.where === 'indoor');
  if (filters.power !== 'all' && scope === 'rides') params.power = String(filters.power === 'with');
  return params;
}

export function useRideFilters(): [RideFilters, (next: RideFilters) => void] {
  const [filters, setFilters] = useState<RideFilters>(() => parseFilters(readStored(RIDE_FILTERS_KEY)));
  const update = (next: RideFilters) => {
    setFilters(next);
    writeStored(RIDE_FILTERS_KEY, JSON.stringify(next));
  };
  return [filters, update];
}

interface SourcesResponse {
  providers?: Record<string, { rides: number }>;
}

/** Providers the rider has rides from, in display order: what the filter offers. */
export function useRideProviders(): Provider[] {
  const { data } = useQuery<SourcesResponse>({
    queryKey: ['rides', 'sources'],
    queryFn: () => axiosClient.get('/rides/sources').then((r) => r.data),
    staleTime: 60_000,
  });
  const counts = data?.providers ?? {};
  return PROVIDERS.filter((p) => (counts[p]?.rides ?? 0) > 0);
}
